import type { DeviceStatus } from '@/gql/graphql';
import { int, oneOf, text, uuid } from '@/hooks/useUrlState';

/** Every `DeviceStatus` in the order the filter lists them, with its label; the `Record` makes the compiler flag a status added to the schema but not here. */
export const DEVICE_STATUS_LABELS: Record<DeviceStatus, string> = {
  REQUESTING: 'requesting',
  CHECKOUT: 'checkout',
  CANCELED: 'canceled',
};

/** The connection states a device reports, in the order the filter lists them. */
export const DEVICE_CONNECTION_LABELS = {
  connected: 'connected',
  screensaver: 'screensaver',
  disconnected: 'disconnected',
};

export type DevicesSortColumn = 'name' | 'status' | 'connection' | 'category' | 'terminal' | 'createdAt';

/** DataGrid column `id` → API `sortField` (see DeviceRepository::searchPaginated); its keys are the values `sort` accepts. */
export const DEVICE_COLUMN_SORT: Record<DevicesSortColumn, string> = {
  name: 'name',
  status: 'status',
  connection: 'connectionStatus',
  category: 'categoryName',
  terminal: 'terminalName',
  createdAt: 'createdAt',
};

export const DEVICES_DEFAULT_SORT: { sort: DevicesSortColumn; dir: 'asc' | 'desc' } = { sort: 'name', dir: 'asc' };

/** The /devices query string, in this order (see README "List pages, URL state"). */
export const devicesUrlSchema = {
  search: text(),
  status: oneOf(DEVICE_STATUS_LABELS, ''),
  connection: oneOf(DEVICE_CONNECTION_LABELS, ''),
  terminal: uuid(),
  category: uuid(),
  sort: oneOf(DEVICE_COLUMN_SORT, DEVICES_DEFAULT_SORT.sort),
  dir: oneOf<'asc' | 'desc', 'asc' | 'desc'>({ asc: true, desc: true }, DEVICES_DEFAULT_SORT.dir),
  page: int({ min: 1 }, 1),
  size: int({ min: 1, max: 500 }, 25),
};

/** The keys that live in the collapsible Filters panel: the panel opens on mount when any is set. */
export const DEVICES_ADVANCED_KEYS = [
  'status',
  'connection',
  'terminal',
  'category',
] as const satisfies readonly (keyof typeof devicesUrlSchema)[];
